import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Modal,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import React from "react";
import { useUpdateReminder } from "../../../stores/Ticketing";
import dayjs from "dayjs";

interface EditReminderDialogProps {
  ticketId?: number;
  open: boolean;
  onClose: () => void;
  reminder: {
    id?: number;
    date: Date | null;
    alternativeMessage?: string;
    isResolved: boolean;
  };
  onSave?: (updatedReminder: {
    id?: number;
    date: Date | null;
    alternativeMessage?: string;
    isResolved: boolean;
  }) => void;
  showStatusSelector?: boolean;
}

const EditReminderDialog: React.FC<EditReminderDialogProps> = ({
  ticketId,
  open,
  onClose,
  reminder,
  onSave,
  showStatusSelector = true,
}) => {
  const [editedReminder, setEditedReminder] = React.useState(reminder);
  const { mutate: updateReminder, isPending: isUpdating } = useUpdateReminder();

  React.useEffect(() => {
    setEditedReminder(reminder);
  }, [reminder]);

  const handleSave = () => {
    if (!editedReminder.date) return;

    if (ticketId && reminder.id) {
      updateReminder(
        {
          ticketId,
          reminderId: reminder.id,
          date: dayjs(editedReminder.date).format('YYYY-MM-DD'),
          alternativeMessage: editedReminder.alternativeMessage,
          isResolved: editedReminder.isResolved,
        },
        {
          onSuccess: () => {
            onSave?.(editedReminder);
            onClose();
          },
        }
      );
    } else {
      // Create mode - just update local state
      onSave?.(editedReminder);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Erinnerung bearbeiten</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DatePicker
                label="Datum"
                value={editedReminder.date}
                minDate={new Date()}
                onChange={(newDate: Date | null) => {
                  setEditedReminder(prev => ({
                    ...prev,
                    date: newDate
                  }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Alternative Botschaften"
              value={editedReminder.alternativeMessage || ""}
              onChange={(e) => {
                setEditedReminder(prev => ({
                  ...prev,
                  alternativeMessage: e.target.value
                }));
              }}
              multiline
              rows={3}
            />
          </Grid>

          {showStatusSelector && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={editedReminder.isResolved ? 'done' : 'pending'}
                  onChange={(e) => {
                    setEditedReminder(prev => ({
                      ...prev,
                      isResolved: e.target.value === 'done'
                    }));
                  }}
                >
                  <MenuItem value="pending">Ausstehend</MenuItem>
                  <MenuItem value="done">Erledigt</MenuItem>
                </TextField>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Abbrechen
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained" 
          color="primary"
          disabled={!editedReminder.date || (reminder.id !== undefined && isUpdating)}
        >
          {reminder.id !== undefined && isUpdating ? 'Speichern...' : 'Speichern'}
        </Button>
      </DialogActions>

      <Modal
        open={isUpdating}
        aria-labelledby="loading-modal-title"
        aria-describedby="loading-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div>
          <CircularProgress size={60} />
        </div>
      </Modal>
    </Dialog>
  );
};

export default EditReminderDialog;